import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBrainStore } from '@/state/brainStore';
import { useSessionStore } from '@/state/sessionStore';
import { customerName, relativeFromIso, simTimeFull, aud } from '@/utils/format';
import type { Channel, EngagementStatus, LeadStage, StudioId } from '@/domain/types';
import {
  Search,
  Users,
  ChevronRight,
  Phone,
  MessageSquare,
  AtSign,
  Mail,
  Footprints,
} from 'lucide-react';
import clsx from 'clsx';

const CHANNEL_ICON: Record<Channel, typeof Phone> = {
  voice: Phone,
  sms: MessageSquare,
  'meta-dm': MessageSquare,
  'ig-dm': AtSign,
  email: Mail,
  'web-form': Mail,
  'walk-in': Footprints,
};

const STAGES: Array<{ value: LeadStage | 'all'; label: string }> = [
  { value: 'all', label: 'Any stage' },
  { value: 'new', label: 'New' },
  { value: 'qualifying', label: 'Qualifying' },
  { value: 'deposit-pending', label: 'Deposit pending' },
  { value: 'booked', label: 'Booked' },
  { value: 'completed', label: 'Completed' },
  { value: 'lapsed', label: 'Lapsed' },
  { value: 'dormant', label: 'Dormant' },
];

const STATUSES: Array<{ value: EngagementStatus | 'all'; label: string }> = [
  { value: 'all', label: 'Any status' },
  { value: 'active', label: 'Active' },
  { value: 'warm', label: 'Warm' },
  { value: 'lapsed', label: 'Lapsed' },
  { value: 'dormant', label: 'Dormant' },
];

const DISPLAY_LIMIT = 100;

export default function Customers() {
  const navigate = useNavigate();
  const customers = useBrainStore((s) => s.customers);
  const tail = useBrainStore((s) => s.tailRecords);
  const studios = useBrainStore((s) => s.studios);
  const assignedStudios = useSessionStore((s) => s.assignedStudios);
  const persona = useSessionStore((s) => s.persona);

  const [search, setSearch] = useState('');
  const [stage, setStage] = useState<LeadStage | 'all'>('all');
  const [status, setStatus] = useState<EngagementStatus | 'all'>('all');
  const [studioFilter, setStudioFilter] = useState<StudioId | 'all'>('all');

  // Persona-scoped baseline.
  const personaStudios = useMemo(
    () =>
      persona === 'georgina' || persona === 'owner'
        ? null
        : new Set(assignedStudios),
    [persona, assignedStudios],
  );

  const heroRecords = useMemo(() => Object.values(customers), [customers]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();

    // Combine hero records + tail records.
    type Row = {
      id: string;
      firstName: string;
      lastName: string;
      phone: string;
      suburb: string;
      lastInteractionIso: string;
      lastChannel: Channel;
      leadScore: number;
      stage?: LeadStage;
      engagement: EngagementStatus;
      studioId: StudioId;
      hero: boolean;
      lifetimeSpendAud?: number;
    };

    const heroRows: Row[] = heroRecords.map((c) => {
      const last = c.conversation[c.conversation.length - 1];
      return {
        id: c.id,
        firstName: c.firstName,
        lastName: c.lastName,
        phone: c.phone,
        suburb: c.suburb,
        lastInteractionIso: last?.timestamp ?? c.source.firstTouchAt,
        lastChannel: last?.channel ?? c.source.channel,
        leadScore: c.leadScore,
        stage: c.leadStage,
        engagement: c.engagementStatus,
        studioId: c.primaryStudioId,
        hero: true,
        lifetimeSpendAud: c.lifetimeSpendAud,
      };
    });

    const tailRows: Row[] = tail.map((t) => ({
      id: t.id,
      firstName: t.firstName,
      lastName: t.lastName,
      phone: t.phone,
      suburb: t.suburb,
      lastInteractionIso: t.lastInteractionAt,
      lastChannel: 'sms' as Channel,
      leadScore: 40,
      engagement: t.engagementStatus,
      studioId: t.primaryStudioId,
      hero: false,
      lifetimeSpendAud: t.lastDepositAud,
    }));

    const all: Row[] = [...heroRows, ...tailRows];

    return all
      .filter((r) => {
        if (personaStudios && !personaStudios.has(r.studioId)) return false;
        if (studioFilter !== 'all' && r.studioId !== studioFilter) return false;
        if (status !== 'all' && r.engagement !== status) return false;
        if (stage !== 'all' && r.stage !== stage) return false;
        if (!q) return true;
        const hay =
          `${r.firstName} ${r.lastName} ${r.phone} ${r.suburb}`.toLowerCase();
        return hay.includes(q);
      })
      .sort((a, b) => {
        if (q) {
          // Prefer hero records when searching.
          if (a.hero && !b.hero) return -1;
          if (!a.hero && b.hero) return 1;
        }
        // Heroes float when not searching too.
        if (a.hero && !b.hero) return -1;
        if (!a.hero && b.hero) return 1;
        return b.leadScore - a.leadScore;
      });
  }, [heroRecords, tail, search, stage, status, studioFilter, personaStudios]);

  const visible = filtered.slice(0, DISPLAY_LIMIT);
  const total = filtered.length;

  const studioList = Object.values(studios).filter(
    (s) => !personaStudios || personaStudios.has(s.id),
  );

  return (
    <section className="px-8 py-8" data-testid="customers-screen">
      <header className="flex items-end justify-between max-w-7xl">
        <div>
          <p className="heading-eyebrow">CRM</p>
          <h1 className="heading-display text-4xl text-cream mt-3">Customers</h1>
          <p className="body-serif-soft text-base mt-4 max-w-2xl">
            One record per person. Every channel rolls up here. Search anyone, filter by stage or
            engagement, click in to reply on any channel.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <KPI label="In scope" value={total.toLocaleString('en-AU')} />
          <KPI
            label="High value"
            value={filtered.filter((r) => r.leadScore >= 80).length.toLocaleString('en-AU')}
            accent
          />
          <KPI
            label="Hero records"
            value={filtered.filter((r) => r.hero).length.toString()}
          />
        </div>
      </header>

      <div className="mt-6 panel p-4 max-w-7xl">
        <div className="grid grid-cols-12 gap-3 items-end">
          <label className="col-span-5 block">
            <span className="label-small-caps">Search</span>
            <div className="mt-2 relative">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-cream-soft"
              />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, phone, or suburb"
                className="w-full bg-ink-deep border border-gold-dim/40 rounded-md pl-9 pr-3 py-2 text-sm text-cream focus:outline-none focus:border-gold-bright"
                data-testid="customers-search"
              />
            </div>
          </label>
          <label className="col-span-2 block">
            <span className="label-small-caps">Stage</span>
            <select
              value={stage}
              onChange={(e) => setStage(e.target.value as LeadStage | 'all')}
              className="mt-2 w-full bg-ink-deep border border-gold-dim/40 rounded-md px-2 py-2 text-xs text-cream"
              data-testid="customers-stage"
            >
              {STAGES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
          <label className="col-span-2 block">
            <span className="label-small-caps">Engagement</span>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as EngagementStatus | 'all')}
              className="mt-2 w-full bg-ink-deep border border-gold-dim/40 rounded-md px-2 py-2 text-xs text-cream"
              data-testid="customers-status"
            >
              {STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
          <label className="col-span-3 block">
            <span className="label-small-caps">Studio</span>
            <select
              value={studioFilter}
              onChange={(e) => setStudioFilter(e.target.value as StudioId | 'all')}
              className="mt-2 w-full bg-ink-deep border border-gold-dim/40 rounded-md px-2 py-2 text-xs text-cream"
              data-testid="customers-studio"
            >
              <option value="all">Any studio in scope</option>
              {studioList.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.brand} · {s.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-3">
          Showing {visible.length.toLocaleString('en-AU')} of{' '}
          {total.toLocaleString('en-AU')} matches
        </p>
      </div>

      <div className="mt-4 panel max-w-7xl overflow-hidden">
        {visible.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <Users size={32} className="text-gold-bright mx-auto" />
            <p className="font-serif text-cream-soft mt-3">
              No customers match this view. Loosen a filter or try a broader search.
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-ink-soft/40">
              <tr className="text-[10px] uppercase tracking-widest text-cream-soft">
                <th className="text-left px-4 py-2">Customer</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Score</th>
                <th className="text-left px-4 py-2">Last touch</th>
                <th className="text-left px-4 py-2">Studio</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => {
                const Icon = CHANNEL_ICON[row.lastChannel] ?? MessageSquare;
                const studio = studios[row.studioId];
                return (
                  <tr
                    key={row.id}
                    onClick={() => navigate(`/customer/${row.id}`)}
                    className="border-t border-gold-dim/15 hover:bg-ink-soft/40 cursor-pointer"
                    data-testid={`customer-row-${row.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {row.hero ? (
                          <span className="h-2 w-2 rounded-full bg-gold-bright" title="Hero record" />
                        ) : (
                          <span className="h-2 w-2 rounded-full bg-cream-soft/30" />
                        )}
                        <div>
                          <p className="font-inter text-cream">
                            {row.firstName} {row.lastName}
                          </p>
                          <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-0.5">
                            {row.phone} · {row.suburb}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={clsx(
                          'pill-cream text-[10px]',
                          row.engagement === 'active'
                            ? 'border-mint/60 text-mint'
                            : row.engagement === 'warm'
                              ? 'border-gold-bright/60 text-gold-bright'
                              : row.engagement === 'lapsed'
                                ? ''
                                : 'border-cream-soft/40 text-cream-soft',
                        )}
                      >
                        {row.engagement}
                      </span>
                      {row.stage ? (
                        <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-1">
                          {row.stage}
                        </p>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 font-mono text-cream">{row.leadScore}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 text-cream-soft">
                        <Icon size={12} className="text-gold-bright" />
                        <span className="text-xs">{relativeFromIso(row.lastInteractionIso)}</span>
                      </div>
                      <p className="font-mono text-[10px] text-cream-soft/70 mt-1">
                        {simTimeFull(row.lastInteractionIso)}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-cream text-xs">{studio?.name ?? row.studioId}</p>
                      <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-0.5">
                        {studio?.brand ?? '...'}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ChevronRight size={14} className="text-gold-bright" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {filtered.length > DISPLAY_LIMIT ? (
        <p className="mt-3 font-inter text-[10px] text-cream-soft uppercase tracking-widest text-center max-w-7xl">
          {(filtered.length - DISPLAY_LIMIT).toLocaleString('en-AU')} more records hidden. Narrow
          with search or filters.
        </p>
      ) : null}
    </section>
  );
}

function KPI({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div
      className={clsx(
        'panel p-3 text-center min-w-[110px]',
        accent ? 'border-gold-bright/40' : '',
      )}
    >
      <p className="label-small-caps">{label}</p>
      <p
        className={clsx(
          'font-cinzel text-xl mt-1',
          accent ? 'text-gold-bright' : 'text-cream',
        )}
      >
        {value}
      </p>
    </div>
  );
}

void aud;
void customerName;
